"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { ArrowLeft, Lock01 } from "@untitledui/icons"
import { useState } from "react"

import { BackgroundPattern } from "@/components/ui/background-pattern"
import { Button } from "@/components/ui/button"
import { cx } from "@/components/ui/cx"
import { FeaturedIcon } from "@/components/ui/featured-icon"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { toFieldError } from "@/lib/utils"
import { identityPasswordResetPath, signInPath } from "@/routes"

// ----------------------------------------------------------------------------
// Form Pattern: Uncontrolled Inputs + FormData (with a twist)
// ----------------------------------------------------------------------------
// See sessions/new.tsx for the general pattern explanation.
//
// This page is a special case: we show live password requirement indicators
// (8+ chars, special character) that need the current password value.
//
// Solution: Keep a local `password` state purely for the validation UI.
// The input is still "uncontrolled" for form submission - we use `onChange`
// only to feed the validation indicators, not to control the input's value.
//
// Notice: No `value` prop on the Input. FormData still collects on submit.
// The `onChange` is React Aria's signature: (value: string) => void
// ----------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type PageProps = {
  errors: Record<string, string>
}

interface ResetPasswordProps {
  sid: string
}

export default function ResetPassword({ sid }: ResetPasswordProps) {
  const { errors } = usePage<PageProps>().props
  const [processing, setProcessing] = useState(false)

  // Track password for validation UI only - not for form submission
  const [password, setPassword] = useState("")

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.put(
      identityPasswordResetPath({ sid }),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <section className="bg-primary min-h-screen overflow-hidden px-4 py-12 md:gap-24 md:px-8 md:pt-24">
      <Head title="Set new password" />
      <div className="sheet mx-auto flex w-full max-w-md flex-col gap-8 px-6 py-8 md:px-10 md:py-10">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="relative">
            <FeaturedIcon
              color="gray"
              className="z-10"
              theme="modern"
              size="xl"
            >
              <Lock01 className="size-7" />
            </FeaturedIcon>
            <BackgroundPattern
              size="lg"
              pattern="grid"
              className="absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:block"
            />
            <BackgroundPattern
              size="md"
              pattern="grid"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:hidden"
            />
          </div>

          <div className="z-10 flex flex-col gap-2 md:gap-3">
            <h1 className="text-display-xs md:text-display-sm text-primary">
              Set new password
            </h1>
            <p className="text-md text-tertiary">
              Your new password must be different to previously used passwords.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="z-10 flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            {/*
              onChange here is only for validation UI - no value prop means
              this is still uncontrolled. FormData handles actual submission.
            */}
            <Input
              label="Password"
              type="password"
              name="password"
              placeholder="••••••••"
              size="md"
              isRequired
              hideRequiredIndicator
              onChange={setPassword}
              isInvalid={!!errors?.password}
              error={toFieldError(errors?.password)}
              autoFocus
              autoComplete="new-password"
            />
            <Input
              label="Confirm password"
              type="password"
              name="password_confirmation"
              placeholder="••••••••"
              size="md"
              isRequired
              hideRequiredIndicator
              isInvalid={!!errors?.password_confirmation}
              error={toFieldError(errors?.password_confirmation)}
              autoComplete="new-password"
            />

            {/* Password requirements - driven by local state */}
            <div className="flex flex-col gap-3">
              <span className="flex items-center gap-2">
                <div
                  className={cx(
                    "flex size-5 items-center justify-center rounded-full transition duration-150 ease-in-out",
                    password.length >= 8
                      ? "bg-do-500 text-ink-950"
                      : "bg-tertiary text-quaternary",
                  )}
                >
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path
                      d="M1.25 4L3.75 6.5L8.75 1.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-tertiary text-sm">
                  Must be at least 8 characters
                </p>
              </span>
              <span className="flex items-center gap-2">
                <div
                  className={cx(
                    "flex size-5 items-center justify-center rounded-full transition duration-150 ease-in-out",
                    /[!@#$%^&*(),.?":{}|<>]/.test(password)
                      ? "bg-do-500 text-ink-950"
                      : "bg-tertiary text-quaternary",
                  )}
                >
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path
                      d="M1.25 4L3.75 6.5L8.75 1.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-tertiary text-sm">
                  Must contain one special character
                </p>
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              size="lg"
              isLoading={processing}
              showTextWhileLoading
            >
              Reset password
            </Button>
          </div>
        </form>

        <div className="z-10 flex justify-center gap-1 text-center">
          <Link
            href={signInPath()}
            className="text-tertiary hover:text-secondary flex items-center gap-2 text-sm font-semibold"
          >
            <ArrowLeft className="size-4" />
            Back to log in
          </Link>
        </div>
      </div>
    </section>
  )
}
